import exifr from 'exifr';

// Default options for exifr
const jpegOptions = {
	chunked: true,
	gps: { skip: ['GPSAltitudeRef'] },
	exif: ['FocalLength', 'FocalLengthIn35mmFormat', 'ExifImageWidth', 'ExifImageHeight'],
	xmp: true,
	icc: false,
	iptc: false,
	jfif: false,
	ihdr: false,
	ifd1: false,
	ifd0: { pick: ['BitsPerSample'] },
};

const tiffOptions = {
	chunked: true,
	chunkSize: 1000000000,
	ifd0: { skip: ['GDALMetadata'] },
	ifd1: false,
	skip: [
		'StripByteCounts',
		'StripOffsets',
		'GeoTiffDoubleParams',
		'TileByteCounts',
		'TileOffsets',
		'TileLength',
		'TileWidth',
	],
};

const optionsByType = {
	'image/tiff': tiffOptions,
	'image/jpeg': jpegOptions,
};

const formatJpgData = (name, exifData) => ({
	image_name: name,
	GPSLatitude: exifData?.['latitude'] ?? null,
	GPSLongitude: exifData?.['longitude'] ?? null,
	GPSAltitude: exifData?.['GPSAltitude'] ?? null,
	RelativeAltitude: exifData?.['RelativeAltitude'] ?? null,
	PixelXDimension: exifData?.['ExifImageWidth'] ?? null,
	PixelYDimension: exifData?.['ExifImageHeight'] ?? null,
	FocalLength: exifData?.['FocalLength'] ?? null,
	FocalLengthIn35mmFilm: exifData?.['FocalLengthIn35mmFormat'] ?? null,
	GimbalYawDegree: exifData?.['GimbalYawDegree'] ?? null,
	GimbalPitchDegree: exifData?.['GimbalPitchDegree'] ?? null,
	GimbalRollDegree: exifData?.['GimbalRollDegree'] ?? null,
	BitsPerSample: Array.from(exifData?.['BitsPerSample'] ?? [8, 8, 8]),
});

const formatTiffData = (name, exifData) => ({
	image_name: name,
	BitsPerSample: Array.from(exifData?.['BitsPerSample'] ?? [8, 8, 8]),
	Compression: exifData?.['Compression'] ?? null,
	ExtraSamples: exifData?.['ExtraSamples'] ?? null,
	GeoTiffAsciiParams: exifData?.['GeoTiffAsciiParams'] ?? null,
	GeoTiffDirectory: Array.from(exifData?.['GeoTiffDirectory'] ?? []),
	ImageHeight: exifData?.['ImageHeight'] ?? null,
	ImageWidth: exifData?.['ImageWidth'] ?? null,
	ModelTiePoint: Array.from(exifData?.['ModelTiePoint'] ?? []),
	PhotometricInterpretation: exifData?.['PhotometricInterpretation'] ?? null,
	PixelScale: Array.from(exifData?.['PixelScale'] ?? []),
	PlanarConfiguration: exifData?.['PlanarConfiguration'] ?? null,
	RowsPerStrip: exifData?.['RowsPerStrip'] ?? null,
	SampleFormat: Array.from(exifData?.['SampleFormat'] ?? []),
	SamplesPerPixel: exifData?.['SamplesPerPixel'] ?? null,
});

const formatByType = {
	'image/tiff': formatTiffData,
	'image/jpeg': formatJpgData,
};

/**
 * A higher-order function that creates a function to format EXIF data based on the file type.
 *
 * @param {string} type - The MIME type of the file ('image/jpeg', 'image/tiff').
 * @param {string} name - The name of the file.
 * @returns {function(Object): *} A function that takes EXIF data and returns formatted data according to the file type.
 * @throws {Error} Throws an error if the type is 'image/jpeg' and no GPS data (latitude or longitude) is found in the image.
 */
const formatExifData = (type, name) => exifData => {
	const { latitude, longitude } = exifData ?? {};
	if (type == 'image/jpeg' && (!latitude || !longitude))
		throw new Error('No GPS data found in image');

	return formatByType[type](name, exifData);
};

/**
 * Parses EXIF metadata from a file using the exifr library.
 *
 * @param {File|Blob} file - The file to extract EXIF data from
 * @param {string} file.type - The MIME type of the file
 * @param {string} file.name - The name of the file
 * @returns {Promise<Object>} A promise that resolves to the formatted EXIF data
 * @throws {Error} If the file type is not found
 */
const parseExif = file => {
	const { type, name } = file ?? {};
	if (!type) throw new Error('File type not found');
	return exifr.parse(file, optionsByType[type]).then(formatExifData(type, name));
};

/**
 * Extracts EXIF data from a collection of files asynchronously
 *
 * @async
 * @function getExifData
 * @param {Array} files - Array of files to extract EXIF data from
 * @returns {Promise<Array>} Promise that resolves to an array of EXIF data objects
 * @throws {Error} If any file's EXIF data extraction fails, throws an Error containing all error messages
 */
export const getExifData = async files => {
	const exifDataPromises = files.map(parseExif);
	return Promise.allSettled(exifDataPromises).then(results => {
		// Filter out only rejected promises
		const errors = results
			.filter(({ status }) => status == 'rejected')
			.map(({ reason }) => reason.message);

		// Handle errors
		if (errors?.length) throw new Error(errors);

		return results.map(({ value }) => value);
	});
};
